<template>
  <RouterView v-slot="{ Component, route }">
    <component :is="getLayout(route)" v-bind="getLayoutProps(route)">
      <ErrorBoundary>
        <KeepAlive :include="keepAliveRoutes" :max="5">
          <component :is="Component" />
        </KeepAlive>
      </ErrorBoundary>
    </component>
  </RouterView>

  <SheetHost />
  <ModalHost />
  <ToastHost />

  <CookieBanner />

  <MobileSearchResults class="tablet-h:hidden" />

  <Transition name="modal">
    <div
      v-if="uploadStore.showUploadBar"
      class="absolute bottom-[calc(env(safe-area-inset-bottom)+93px)] left-0 z-40 flex w-full justify-center tablet-h:bottom-2.5 tablet-h:z-100"
    >
      <UploadBar />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import {
  type Component,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  watch,
} from 'vue'
import {
  type RouteLocationNormalizedLoaded,
  parseQuery,
  RouterView,
  useRoute,
  useRouter,
} from 'vue-router'
import {
  useMeQuery,
  useBillingQueryQuery,
  type BillingResponse,
  type PlanResponse,
  useOnboardingQuery,
} from '@/shared/graphql/legacy/generated'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/shared/graphql/router'
import { getTokens, isSignedIn, setupAuth } from '@dacr/auth'
import { decodeJWT } from '@/shared/utils/jwt'

import DefaultLayout from '@/layouts/DefaultLayout.vue'
import ShellLayout from '@/layouts/ShellLayout.vue'
import BasicLayout from '@/layouts/BasicLayout.vue'
import ErrorBoundary from '@/shared/components/ErrorBoundary.vue'

import { ModalHost, SheetHost, ToastHost } from '@dacr/library'
import CookieBanner from '@/shared/components/CookieBanner.vue'
import MobileSearchResults from '@/features/search/components/MobileSearchResults.vue'
import UploadBar from '@/features/upload/components/UploadBar.vue'

import { useUserStore } from '@/shared/stores/user'
import { analytics } from '@/shared/services/analytics'
import { useBillingStore } from '@/features/billing/store'
import { useUploadStore } from '@/features/upload/store'
import {
  NativeActionsEnum,
  useWebviewStore,
  type PostMessageData,
} from '@/shared/stores/webview'
import { usePlatformStore } from '@/shared/stores/platform'
import { useOnboardingStore } from '@/features/onboarding/store'
import { useOriginStore } from '@/shared/stores/origin'

import { useEventListener } from '@/shared/composables/useEventListener'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { isMaintenanceMode } from '@/shared/utils/maintenance'

const PRO_GRANT_RECONCILE_MS = 5_000

const iOSWebviewSafeAreaResponseKey = '{"type":"webview:init"}'

interface iOSWebviewSafeAreaResponse {
  [iOSWebviewSafeAreaResponseKey]: {
    top?: number
    bottom?: number
  }
}

const layoutMap: Record<string, Component> = {
  default: DefaultLayout,
  shell: ShellLayout,
  basic: BasicLayout,
}

const getLayout = (route: RouteLocationNormalizedLoaded) => {
  const layout = route.meta.layout

  if (typeof layout !== 'string') {
    return 'div'
  }

  return layoutMap[layout] || DefaultLayout
}

const getLayoutProps = (route: RouteLocationNormalizedLoaded) => {
  if (typeof route.meta.layout !== 'string') {
    return {
      class: 'min-h-dvh bg-white',
    }
  }

  return {
    sidebar: !!route.meta.sidebar,
  }
}

const keepAliveRoutes = [
  'FileExplorerFolderView',
  'CopyrightView',
  'FileExplorerView',
]

const userStore = useUserStore()
const billingStore = useBillingStore()
const uploadStore = useUploadStore()
const webviewStore = useWebviewStore()
const platformStore = usePlatformStore()
const onboardingStore = useOnboardingStore()
const originStore = useOriginStore()
const route = useRoute()
const router = useRouter()

const enabled = computed(() => !!userStore.isSignedIn)

const { result: userData, refetch: refetchUser } = useMeQuery({
  enabled,
  pollInterval: 60_000,
})

const { result: billingData } = useBillingQueryQuery(
  {},
  {
    enabled,
    pollInterval: 60_000,
  }
)

const { result: onboardingData } = useOnboardingQuery({
  enabled,
})

const { result: originsResult } = useQuery(
  graphql(`
    query Origins {
      origins {
        id
        name
        currency
        flag {
          url
        }
        logo {
          white {
            url
          }
          black {
            url
          }
        }
      }
    }
  `),
  {},
  {
    enabled,
    clientId: 'router',
  }
)

watch(userData, () => {
  if (!userData.value?.me) return

  userStore.setUser(userData.value.me)
})

let proGrantTimer: ReturnType<typeof setInterval> | undefined

const stopProGrantReconcile = () => {
  if (!proGrantTimer) return

  clearInterval(proGrantTimer)
  proGrantTimer = undefined
}

watch(
  () => userStore.pendingProGrant,
  (expiresAt) => {
    stopProGrantReconcile()

    if (!expiresAt) return

    proGrantTimer = setInterval(() => {
      if (userStore.pendingProGrant <= Date.now()) {
        userStore.clearProGrantPending()
        return
      }

      if (userStore.isSignedIn) refetchUser()
    }, PRO_GRANT_RECONCILE_MS)
  },
  { immediate: true }
)

onUnmounted(stopProGrantReconcile)

watch(billingData, () => {
  if (!billingData.value?.billing) return

  billingStore.setBilling(billingData.value.billing as BillingResponse)
})

watch(
  onboardingData,
  () => {
    if (
      !onboardingData.value ||
      onboardingData.value.onboarding.completed ||
      route.path.includes('register') ||
      webviewStore.isActive
    )
      return

    if (onboardingData.value.onboarding.step === onboardingStore.maxSteps) {
      router.push('/onboarding/pro')
    } else {
      onboardingStore.step = (onboardingData.value.onboarding.step || 0) + 1
      router.push('/onboarding')
    }
  },
  {
    immediate: true,
    deep: true,
  }
)

watch(originsResult, () => {
  if (!originsResult.value?.origins) return

  originStore.setOrigins(originsResult.value.origins)
})

watch(
  () => webviewStore.topSafeAreaHeight,
  (top) => {
    if (!top) return

    document.documentElement.style.setProperty(
      '--sheet-safe-area-top',
      `${top}px`
    )
  }
)

watch(
  () => userStore.user?.showUpdatedTerms,
  (showUpdatedTerms) => {
    if (!showUpdatedTerms || webviewStore.isActive) return
    // Nothing to accept while the API is down, and on mobile the terms sheet
    // would render alongside the maintenance modal in a separate host.
    if (isMaintenanceMode()) return

    openDialog(Dialog.UpdatedTerms, {
      preventOutsideClick: true,
      hideClose: true,
      disableDrag: true,
    })
  },
  { immediate: true }
)

const handleShowUpgradeBundle = async () => {
  if (!billingStore.canUpdateSubscription) {
    openDialog(Dialog.IAP, {
      props: { type: 'change' },
    })
    return
  }

  await router.push('/settings/billing')

  setTimeout(() => {
    openModal(Modals.UpgradeBundleLimit)
  }, 500)
}

const handleWebviewMessage = async (event: MessageEvent) => {
  if (event.origin !== import.meta.env.VITE_URL) return

  const data = event.data as PostMessageData

  if (data.type === NativeActionsEnum.Plans) {
    const prev = JSON.stringify(billingStore.webviewPlans)
    const next = JSON.stringify(data.data)
    if (prev === next) return

    billingStore.setWebviewPlans(data.data as PlanResponse)
  }

  if (data.type === NativeActionsEnum.ShowUpgradeBundle) {
    await handleShowUpgradeBundle()
  }

  if (data.type === NativeActionsEnum.Locale) {
    platformStore.setLocale(data.data as string)
  }

  if (data.type === NativeActionsEnum.Resubscribe) {
    if (!billingStore.canUpdateSubscription) {
      openDialog(Dialog.IAP, {
        props: { type: 'change' },
      })
      return
    }

    openDialog(Dialog.ResubscribeIAP)
  }
}

onBeforeMount(async () => {
  if (!webviewStore.isActive) {
    await setupAuth({
      url: import.meta.env.VITE_AUTH_API,
      debug: import.meta.env.MODE === 'prod',
    })
  }

  analytics.configurePageTracking(
    isSignedIn() ? decodeJWT(getTokens().accessToken!)?.uid : undefined
  )
})

onMounted(async () => {
  if (isMaintenanceMode()) {
    openModal(Modals.Maintenance, {
      preventOutsideClick: true,
      hideClose: true,
    })
  }

  if (webviewStore.isActive) {
    useEventListener(window, 'message', handleWebviewMessage)

    const data = await webviewStore.postMessage({
      type: NativeActionsEnum.WebviewInit,
    })

    if (data && webviewStore.isiOS) {
      const webviewData = data as iOSWebviewSafeAreaResponse

      webviewStore.setTopSafeAreaheight(
        webviewData[iOSWebviewSafeAreaResponseKey].top || 0
      )
      webviewStore.setBottomSafeAreaHeight(
        webviewData[iOSWebviewSafeAreaResponseKey].bottom || 0
      )
    }
  }

  const searchParams = parseQuery(window.location.search)

  if (searchParams.showUpgradeBundle) {
    handleShowUpgradeBundle()
  }
})
</script>

<style>
* {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-variation-settings: 'slnt' 0;
  background-clip: padding-box;
}

html {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
}

#app {
  background-color: black;
}

#card > .__PrivateStripeElement {
  width: 100%;
}
</style>
